import { memo, useCallback, useState, type JSX } from 'react'

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { pick } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { useColumns, NewDialog, RowActions } from 'domains/MediaChannels/Entries'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { mediaChannelEntriesOverviewRoute } from 'pages/NWPlatform/MediaChannels/MediaChannel'
import { mediaChannelEntryIndexRoute } from 'pages/NWPlatform/MediaChannels/MediaChannel/MediaChannelEntry'
import { mediaChannelQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain'])
  const queryClient = useQueryClient()

  const [newDialogOpen, setNewDialogOpen] = useState(false)

  const navigate = useNavigate({ from: mediaChannelEntriesOverviewRoute.fullPath })
  const { mediaChannelId } = mediaChannelEntriesOverviewRoute.useParams()
  const { query } = mediaChannelEntriesOverviewRoute.useSearch()

  const mediaChannelEntriesQuery = useInfiniteQuery({
    ...mediaChannelQueryKeys.entryList({
      channelId: mediaChannelId,
      query,
    }),
    select: (data) => ({ flat: data.pages.flatMap((page) => page.items) }),
  })
  const mediaChannelEntries = mediaChannelEntriesQuery.data?.flat

  const columns = useColumns()

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:MediaChannel.searchMediaChannelEntry')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () =>
              queryClient.invalidateQueries({
                queryKey: mediaChannelQueryKeys.entryList({ channelId: mediaChannelId, query }).queryKey,
              }),
            [mediaChannelId, query, queryClient],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        size={56}
        data={mediaChannelEntries ?? []}
        columns={columns}
        isInitialLoadingGlobal={mediaChannelEntriesQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={mediaChannelEntriesOverviewRoute.fullPath}
            to={mediaChannelEntryIndexRoute.to}
            params={{ mediaChannelEntryId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id']}
        enableInfinity
        infinityProps={pick(mediaChannelEntriesQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('domain:MediaChannel.mediaChannel'),
        })}
        RowActions={(props) => <RowActions {...props} />}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
